import Breadcrumbs from '@/components/Breadcrumbs';
import PageTitle from '@/components/PageTitle';
import ThemeWrapper from '@/components/ThemeWrapper';
import { useThemeColors } from '@/hooks/useThemeColors';
import useRoutes from '@/hooks/useRoutes';
import AppLayout from '@/layouts/AppLayout';
import { AmaRecord, AmaSeries, CourseData } from '@/types';
import AmaDescriptionBanner from '@/components/course/AmaDescriptionBanner';
import { Link, usePage } from '@inertiajs/react';
import { ChevronRight, Play, Video } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    course: CourseData;
    ama_series: AmaSeries;
    ama_description: { bunny_url: string | null; description: string | null } | null;
};

const AmaSeriesDetailContent: React.FC = () => {
    const { t } = useTranslation();
    const { route } = useRoutes();
    const { props } = usePage<Props>();
    const { course, ama_series, ama_description } = props;
    const colors = useThemeColors();

    const records: AmaRecord[] = ama_series.records || [];

    const breadcrumbItems = [
        {
            label: t('course.sectionDetail.courses'),
            href: route('my-account', { tab: 'courses' }).toString(),
        },
        {
            label: course.title,
            href: `/courses/${course.slug}/`,
        },
        {
            label: ama_series.section?.title || 'AMA',
            href: `/courses/${course.slug}/sections/${ama_series.section?.id}/`,
        },
        { label: ama_series.title },
    ];

    return (
        <AppLayout>
            <PageTitle title={ama_series.title} />
            <div className="min-h-screen bg-gray-50">
                <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 lg:px-8">
                    {/* Breadcrumbs */}
                    <div className="mb-6">
                        <Breadcrumbs items={breadcrumbItems} />
                    </div>

                    {/* AMA Description (global) */}
                    {ama_description && (
                        <AmaDescriptionBanner amaDescription={ama_description} />
                    )}

                    {/* Header */}
                    <div className="mb-8 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
                        <div className="flex items-center gap-4">
                            <div className={`flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl ${colors.primaryBg}`}>
                                <Video size={24} className={colors.iconColor} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-gray-900">
                                    {ama_series.title}
                                </h1>
                                <p className="mt-1 text-sm text-gray-500">
                                    {records.length} {t('course.sessions', 'sesiones')}
                                </p>
                            </div>
                        </div>

                        {ama_series.description && (
                            <p className="mt-4 text-gray-600">{ama_series.description}</p>
                        )}
                    </div>

                    {/* Records List */}
                    {records.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
                                <Video className="h-8 w-8 text-gray-400" />
                            </div>
                            <h3 className="mb-2 text-lg font-semibold text-gray-900">
                                {t('course.noRecordsAvailable', 'No hay grabaciones disponibles')}
                            </h3>
                            <p className="text-sm text-gray-500">
                                {t('course.noRecordsDescription', 'Las grabaciones aparecerán aquí cuando estén disponibles.')}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                            <div className="divide-y divide-gray-100">
                                {records.map((record, index) => (
                                    <Link
                                        key={record.id}
                                        href={`/courses/${course.slug}/ama-series/${ama_series.id}/records/${record.id}/`}
                                        className="group flex items-center gap-4 px-6 py-4 transition-colors duration-150 hover:bg-gray-50"
                                    >
                                        <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 ${colors.border} ${colors.accentBg}`}>
                                            <Play size={12} fill="currentColor" className={colors.iconColor} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs font-medium text-gray-400">
                                                    {index + 1}.
                                                </span>
                                                <span className={`font-medium text-gray-900 group-hover:${colors.iconColor} transition-colors`}>
                                                    {record.title}
                                                </span>
                                            </div>
                                        </div>
                                        <ChevronRight
                                            size={16}
                                            className={`flex-shrink-0 text-gray-400 transition-colors group-hover:${colors.iconColor}`}
                                        />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </AppLayout>
    );
};

const AmaSeriesDetail: React.FC<Props> = () => {
    return (
        <ThemeWrapper>
            <AmaSeriesDetailContent />
        </ThemeWrapper>
    );
};

export default AmaSeriesDetail;
